import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * XSS-safe markdown renderer for schema descriptions
 * Supports: bold, italic, links, line breaks
 * Blocks: HTML, scripts, images, iframes, and other dangerous content
 */

// Validate URLs to prevent XSS attacks via javascript: protocol
const sanitizeUrl = (url) => {
    // Only allow safe protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:', '#'];
    
    try {
        // Handle relative URLs and anchors
        if (url.startsWith('#') || url.startsWith('/')) {
            return url;
        }
        
        const parsed = new URL(url);
        return allowedProtocols.some(protocol => 
            parsed.protocol === protocol || url.startsWith(protocol)
        ) ? url : '#';
    } catch {
        // Invalid URL or relative path
        return url.startsWith('/') ? url : '#';
    }
};

export const MarkdownDescription = ({ 
    content, 
    variant = 'body2', 
    color = 'text.secondary',
    component = 'div',
    ...otherProps 
}) => {
    const theme = useTheme();

    if (!content) {
        return null;
    }

    // Custom component renderers for markdown elements
    const components = {
        // Paragraph renderer
        p: ({ children }) => (
            <Typography 
                variant={variant} 
                color={color}
                component="span"
                sx={{ display: 'block', mb: 1.5 }}
            >
                {children}
            </Typography>
        ),
        
        // Strong/bold renderer
        strong: ({ children }) => (
            <Typography
                component="span"
                sx={{ fontWeight: 500 }}
            >
                {children}
            </Typography>
        ),
        
        // Emphasis/italic renderer
        em: ({ children }) => (
            <Typography
                component="span"
                sx={{ fontStyle: 'italic' }}
            >
                {children}
            </Typography>
        ),
        
        // Link renderer with security
        a: ({ href, children }) => {
            const safeUrl = sanitizeUrl(href || '#');
            const isExternal = safeUrl.startsWith('http');
            
            return (
                <Link
                    href={safeUrl}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noopener noreferrer' : undefined}
                    sx={{
                        color: theme.palette.primary.main,
                        textDecoration: 'underline',
                        '&:hover': {
                            color: theme.palette.primary.dark,
                        }
                    }}
                    onClick={(e) => {
                        // Additional safety: prevent navigation if URL seems suspicious
                        if (safeUrl === '#' && href !== '#') {
                            e.preventDefault();
                        }
                    }}
                >
                    {children}
                </Link>
            );
        },
        
        // Code inline renderer (for backticks)
        code: ({ children }) => (
            <Typography
                component="code"
                sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.9em',
                    backgroundColor: theme.palette.action.hover,
                    padding: '2px 4px',
                    borderRadius: '3px',
                }}
            >
                {children}
            </Typography>
        ),
        
        // List renderers
        ul: ({ children }) => (
            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {children}
            </ul>
        ),
        ol: ({ children }) => (
            <ol style={{ margin: '4px 0', paddingLeft: '20px' }}>
                {children}
            </ol>
        ),
        li: ({ children }) => (
            <Typography component="li" variant={variant} color={color} sx={{ mb: 0.5 }}>
                {children}
            </Typography>
        ),

        // Line break renderer
        br: () => <br />,
    };

    return (
        <Typography 
            component={component} 
            variant={variant} 
            color={color}
            {...otherProps}
        >
            <ReactMarkdown
                // Security: Skip HTML completely (strips all HTML tags)
                skipHtml={true}
                
                // Only allow safe markdown elements (whitelist approach)
                allowedElements={['p', 'strong', 'em', 'a', 'code', 'br', 'ul', 'ol', 'li']}
                
                // Use our custom component renderers for safe rendering
                components={components}
                
                // Transform URLs for safety (validates protocols)
                urlTransform={sanitizeUrl}
            >
                {content}
            </ReactMarkdown>
        </Typography>
    );
};

// Variant specifically for tooltips (more compact)
export const MarkdownTooltip = ({ content }) => {
    return (
        <MarkdownDescription
            content={content}
            variant="body2"
            sx={{ 
                '& p': { mb: 0.5 },
                '& p:last-child': { mb: 0 }
            }}
        />
    );
};